import { motion } from "framer-motion";
import { cta } from "@/lib/site";
import { ArrowUpRight } from "lucide-react";

const CHAPTERS = [
  {
    n: "01",
    title: "Everyone else is fighting for the inbox.",
    body:
      "Your prospects delete 205 emails a day, ignore 14 push notifications, and swipe past a thousand ads. Then they walk to the mailbox and open every single thing they find. That’s where you should be.",
    kicker: "The attention arbitrage",
    image:
      "https://images.unsplash.com/photo-1637597384611-0c33cef6ec03?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwyfHxwb3N0Y2FyZHMlMjBzdGFja2VkfGVufDB8fHx8MTc4NDIzMjY3M3ww&ixlib=rb-4.1.0&q=85",
    rotate: "-rotate-3",
    align: "left",
  },
  {
    n: "02",
    title: "Handwriting beats headlines.",
    body:
      "Every card is written by our robotic pen-arms using real ballpoint ink on real cardstock. It looks — because it is — handwritten. Response rates jump 4–7× when a piece of mail looks personal. This is that piece of mail.",
    kicker: "Ink you can feel",
    image:
      "https://images.unsplash.com/photo-1599498448014-81d90414c50a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwyfHxoYW5kd3JpdHRlbiUyMG5vdGUlMjBwYXBlcnxlbnwwfHx8fDE3ODQyMzI2NzN8MA&ixlib=rb-4.1.0&q=85",
    rotate: "rotate-2",
    align: "right",
  },
  {
    n: "03",
    title: "Automated. But nothing about it feels automated.",
    body:
      "Upload a list, drop in a template, pick a trigger. New lead → thank-you card. Closed deal → congratulations. Cold list → the message that finally lands. Thanks.io prints, stamps, and mails within 48 hours while you go do literally anything else.",
    kicker: "Set once. Warm forever.",
    image:
      "https://images.unsplash.com/photo-1578531504112-0dcd95eda450?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHw0fHxwb3N0YWwlMjBzdGFtcCUyMG1haWx8ZW58MHx8fHwxNzg0MjMyNjczfDA&ixlib=rb-4.1.0&q=85",
    rotate: "-rotate-2",
    align: "left",
  },
  {
    n: "04",
    title: "One card. Or one hundred thousand.",
    body:
      "Real estate farm mailings. Post-purchase thank-yous. Local business grand openings. Investor seller finder campaigns. Nonprofit donor love. Whatever your growth motion is — it fits on a 4×6 card and it costs less than a coffee.",
    kicker: "Send at the speed of business",
    image:
      "https://images.unsplash.com/photo-1584382179644-9c11f8e6ee0c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwzfHxwb3N0YWwlMjBzdGFtcCUyMG1haWx8ZW58MHx8fHwxNzg0MjMyNjczfDA&ixlib=rb-4.1.0&q=85",
    rotate: "rotate-3",
    align: "right",
  },
];

const numberVariant = {
  hidden: { y: 60, opacity: 0 },
  show: {
    y: 0,
    opacity: 1,
    transition: { duration: 1.1, ease: [0.22, 1, 0.36, 1] },
  },
};

const imgVariant = {
  hidden: { opacity: 0, scale: 0.9, y: 30 },
  show: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: { duration: 1.1, ease: [0.22, 1, 0.36, 1] },
  },
};

const textVariant = {
  hidden: { opacity: 0, y: 30 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.15 },
  },
};

export const Manifesto = () => {
  return (
    <section
      id="manifesto"
      className="relative py-24 md:py-40"
      data-testid="manifesto-section"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-3xl mb-24 md:mb-32"
        >
          <div className="postmark text-[#D93829] mb-6">
            <span className="w-1 h-1 rounded-full bg-[#D93829]" />
            The Manifesto
          </div>
          <h2 className="font-serif-display text-4xl md:text-6xl leading-[1.05] tracking-tight text-[#1C1C1A]">
            Four reasons the mailbox is your{" "}
            <span className="italic text-[#D93829]">unfair advantage</span>.
          </h2>
        </motion.div>

        <div className="space-y-40 md:space-y-56">
          {CHAPTERS.map((c, i) => {
            const alignLeft = c.align === "left";
            return (
              <motion.article
                key={c.n}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-120px" }}
                className="grid grid-cols-12 gap-6 md:gap-10 items-center"
                data-testid={`chapter-${c.n}`}
              >
                {/* Image */}
                <motion.div
                  variants={imgVariant}
                  className={`col-span-12 md:col-span-5 ${
                    alignLeft ? "md:order-1" : "md:order-3"
                  }`}
                >
                  <div className={`polaroid ${c.rotate} inline-block`}>
                    <img
                      src={c.image}
                      alt={c.title}
                      className="w-full max-w-[420px] h-[280px] md:h-[340px] object-cover"
                    />
                    <div className="mt-3 flex items-center justify-between text-[11px] uppercase tracking-[0.2em] text-[#68655E]">
                      <span>Chapter {c.n}</span>
                      <span className="font-hand text-[15px] text-[#D93829] normal-case tracking-normal">
                        {c.kicker}
                      </span>
                    </div>
                  </div>
                </motion.div>

                {/* Number */}
                <motion.div
                  variants={numberVariant}
                  className={`hidden md:block md:col-span-2 ${
                    alignLeft ? "md:order-2" : "md:order-1"
                  }`}
                >
                  <div className="font-serif-display italic text-[#D93829]/90 text-[140px] leading-none tracking-tighter">
                    {c.n}
                  </div>
                </motion.div>

                {/* Body */}
                <motion.div
                  variants={textVariant}
                  className={`col-span-12 md:col-span-5 ${
                    alignLeft ? "md:order-3" : "md:order-2"
                  }`}
                >
                  <div className="md:hidden font-serif-display italic text-[#D93829] text-6xl leading-none mb-4">
                    {c.n}
                  </div>
                  <div className="text-[11px] uppercase tracking-[0.24em] text-[#68655E] mb-4">
                    {c.kicker}
                  </div>
                  <h3 className="font-serif-display text-3xl md:text-4xl leading-[1.1] tracking-tight text-[#1C1C1A] mb-6">
                    {c.title}
                  </h3>
                  <p className="text-lg md:text-xl leading-relaxed text-[#3B3A36] font-sans-body">
                    {c.body}
                  </p>
                </motion.div>
              </motion.article>
            );
          })}
        </div>

        {/* CTA end-of-manifesto */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="mt-32 md:mt-40 flex flex-col md:flex-row items-start md:items-center justify-between gap-8 border-t border-[#E4DFD4] pt-10"
        >
          <p className="font-serif-display italic text-2xl md:text-3xl text-[#1C1C1A] max-w-xl">
            “The mail is the last channel that hasn’t been ruined by
            algorithms.”
          </p>
          <a
            href={cta("manifesto")}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="manifesto-cta"
            className="group inline-flex items-center gap-3 bg-[#1C1C1A] text-[#F7F5F0] px-8 py-4 rounded-full text-sm uppercase tracking-[0.2em] font-semibold hover:bg-[#D93829] transition-colors duration-500"
          >
            Send my first card
            <ArrowUpRight
              size={16}
              className="transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1"
            />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default Manifesto;
